import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/lib/auth";
import AvatarUpload from "@/components/app/AvatarUpload";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { toast } from "sonner";
import {
  Mail, Phone, MapPin, Calendar, Edit, Save, X, Building2, User2,
  Heart, Home, Droplet, Sparkles
} from "lucide-react";

export default function EmployeeProfile() {
  const { id } = useParams();
  const { user } = useAuth();
  const [e, setE] = useState(null);
  const [completion, setCompletion] = useState(null);
  const [depts, setDepts] = useState([]);
  const [emps, setEmps] = useState([]);
  const [resps, setResps] = useState([]);
  const [notes, setNotes] = useState([]);
  const [editOpen, setEditOpen] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);
  const [editStatutory, setEditStatutory] = useState(false);
  const [editBank, setEditBank] = useState(false);
  const [editFamily, setEditFamily] = useState(false);
  const [form, setForm] = useState({});
  const [assign, setAssign] = useState({ skills: "", responsibility_ids: [], dotted_manager_id: "", emergency_contact: "", address: "", blood_group: "", employee_type: "" });
  const [noteTxt, setNoteTxt] = useState("");

  const isMe = user?.employee_id === id;
  const canEdit = user?.role === "super_admin" || user?.role === "hr_admin" || isMe;
  const canAssign = ["super_admin", "hr_admin"].includes(user?.role);
  const canViewNotes = ["super_admin", "hr_admin", "manager", "department_head"].includes(user?.role);

  const load = async () => {
    const [emp, comp, dep, allEmps, rsp] = await Promise.all([
      api.get(`/employees/${id}`),
      api.get(`/employees/${id}/completion`),
      api.get("/departments"),
      api.get("/employees"),
      api.get("/responsibilities"),
    ]);
    setE(emp.data); setCompletion(comp.data); setDepts(dep.data); setEmps(allEmps.data); setResps(rsp.data);
    setForm({
      full_name: emp.data.full_name,
      phone: emp.data.phone || "",
      designation: emp.data.designation || "",
      department_id: emp.data.department_id || "",
      manager_id: emp.data.manager_id || "",
      location: emp.data.location || "",
    });
    setAssign({
      skills: (emp.data.skills || []).join(", "),
      responsibility_ids: emp.data.responsibility_ids || [],
      dotted_manager_id: emp.data.dotted_manager_id || "",
      emergency_contact: emp.data.emergency_contact || "",
      address: emp.data.address || "",
      blood_group: emp.data.blood_group || "",
      employee_type: emp.data.employee_type || "",
    });
    if (canViewNotes) {
      try { const n = await api.get(`/employees/${id}/notes`); setNotes(n.data); } catch { /* ignore */ }
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  if (!e) return <div className="p-8 text-slate-500">Loading…</div>;

  const saveEdit = async () => {
    try {
      const payload = { ...form, manager_id: form.manager_id || null, department_id: form.department_id || null };
      await api.patch(`/employees/${id}`, payload);
      toast.success("Profile updated"); setEditOpen(false); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const saveAssign = async () => {
    try {
      const payload = {
        skills: assign.skills.split(",").map(s => s.trim()).filter(Boolean),
        responsibility_ids: assign.responsibility_ids,
        dotted_manager_id: assign.dotted_manager_id || null,
        emergency_contact: assign.emergency_contact,
        address: assign.address,
        blood_group: assign.blood_group,
        employee_type: assign.employee_type,
      };
      await api.patch(`/employees/${id}/assign`, payload);
      toast.success("Assignments updated"); setAssignOpen(false); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const addNote = async () => {
    if (!noteTxt.trim()) return;
    try { await api.post(`/employees/${id}/notes`, { text: noteTxt }); setNoteTxt(""); const n = await api.get(`/employees/${id}/notes`); setNotes(n.data); toast.success("Note added"); }
    catch { toast.error("Failed"); }
  };

  const dept = depts.find(d => d.id === e.department_id);
  const resp_assigned = resps.filter(r => (e.responsibility_ids || []).includes(r.id));

  return (
    <div className="p-6 lg:p-8 space-y-5" data-testid="employee-profile">
      {/* Header */}
      <Card className="p-6 border-slate-200 shadow-none">
        <div className="flex flex-wrap items-start gap-5">
          <AvatarUpload employee={e} size="xl" editable={canEdit} onUpdate={load} />
          <div className="flex-1 min-w-[260px]">
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="text-2xl font-[Outfit] font-semibold text-slate-900">{e.full_name}</h1>
              <Badge className={`border-0 capitalize ${e.employment_status === "active" ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-600"}`}>{e.employment_status}</Badge>
              <Badge variant="outline" className="capitalize">{e.role.replace("_", " ")}</Badge>
              {e.employee_type && <Badge variant="outline" className="capitalize">{e.employee_type}</Badge>}
            </div>
            <div className="text-slate-600 mt-1">{e.designation} · {dept?.name || "—"}</div>
            <div className="mt-3 flex flex-wrap gap-4 text-sm text-slate-600">
              <span className="flex items-center gap-1.5"><Mail className="w-4 h-4 text-slate-400"/>{e.email}</span>
              {e.phone && <span className="flex items-center gap-1.5"><Phone className="w-4 h-4 text-slate-400"/>{e.phone}</span>}
              {e.location && <span className="flex items-center gap-1.5"><MapPin className="w-4 h-4 text-slate-400"/>{e.location}</span>}
              {e.joining_date && <span className="flex items-center gap-1.5"><Calendar className="w-4 h-4 text-slate-400"/>Joined {new Date(e.joining_date).toLocaleDateString()}</span>}
            </div>
          </div>
          {canEdit && (
            <div className="flex gap-2">
              <Dialog open={editOpen} onOpenChange={setEditOpen}>
                <DialogTrigger asChild><Button variant="outline" data-testid="edit-profile-btn"><Edit className="w-4 h-4 mr-1"/>Edit</Button></DialogTrigger>
                <DialogContent className="max-w-lg">
                  <DialogHeader><DialogTitle>Edit Profile</DialogTitle></DialogHeader>
                  <div className="space-y-3 py-2">
                    <div className="grid grid-cols-2 gap-3">
                      <div><Label>Full Name</Label><Input value={form.full_name || ""} onChange={(ev) => setForm({...form, full_name: ev.target.value})} className="mt-1"/></div>
                      <div><Label>Phone</Label><Input value={form.phone} onChange={(ev) => setForm({...form, phone: ev.target.value})} className="mt-1"/></div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div><Label>Designation</Label><Input value={form.designation} onChange={(ev) => setForm({...form, designation: ev.target.value})} className="mt-1"/></div>
                      <div><Label>Location</Label><Input value={form.location} onChange={(ev) => setForm({...form, location: ev.target.value})} className="mt-1"/></div>
                    </div>
                    {canAssign && (
                      <>
                        <div><Label>Department</Label>
                          <Select value={form.department_id} onValueChange={(v) => setForm({...form, department_id: v})}>
                            <SelectTrigger className="mt-1"><SelectValue/></SelectTrigger>
                            <SelectContent>{depts.map(d => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent>
                          </Select>
                        </div>
                        <div><Label>Reporting Manager</Label>
                          <Select value={form.manager_id} onValueChange={(v) => setForm({...form, manager_id: v})}>
                            <SelectTrigger className="mt-1"><SelectValue placeholder="Select"/></SelectTrigger>
                            <SelectContent>{emps.filter(em => em.id !== id).map(em => <SelectItem key={em.id} value={em.id}>{em.full_name}</SelectItem>)}</SelectContent>
                          </Select>
                        </div>
                      </>
                    )}
                  </div>
                  <DialogFooter><Button onClick={saveEdit} className="bg-slate-900 hover:bg-slate-800" data-testid="save-profile-btn"><Save className="w-4 h-4 mr-1"/>Save</Button></DialogFooter>
                </DialogContent>
              </Dialog>
              {canAssign && (
                <Dialog open={assignOpen} onOpenChange={setAssignOpen}>
                  <DialogTrigger asChild><Button variant="outline" data-testid="assign-btn">Assign</Button></DialogTrigger>
                  <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
                    <DialogHeader><DialogTitle>Assign Responsibilities & Details</DialogTitle></DialogHeader>
                    <div className="space-y-3 py-2">
                      <div><Label>Skills (comma-separated)</Label><Input value={assign.skills} onChange={(ev) => setAssign({...assign, skills: ev.target.value})} className="mt-1" placeholder="Python, Leadership, SQL"/></div>
                      <div>
                        <Label>Responsibilities</Label>
                        <div className="mt-1 space-y-1 max-h-48 overflow-y-auto border border-slate-200 rounded-md p-2">
                          {resps.map(r => (
                            <label key={r.id} className="flex items-center gap-2 p-1 hover:bg-slate-50 rounded cursor-pointer">
                              <input type="checkbox" checked={assign.responsibility_ids.includes(r.id)} onChange={(ev) => {
                                setAssign({...assign, responsibility_ids: ev.target.checked
                                  ? [...assign.responsibility_ids, r.id]
                                  : assign.responsibility_ids.filter(x => x !== r.id)});
                              }}/>
                              <div className="text-sm"><span className="font-medium">{r.name}</span> <span className="text-xs text-slate-500">· {r.category}</span></div>
                            </label>
                          ))}
                        </div>
                      </div>
                      <div><Label>Dotted-line Manager</Label>
                        <Select value={assign.dotted_manager_id} onValueChange={(v) => setAssign({...assign, dotted_manager_id: v})}>
                          <SelectTrigger className="mt-1"><SelectValue placeholder="None"/></SelectTrigger>
                          <SelectContent>{emps.filter(em => em.id !== id).map(em => <SelectItem key={em.id} value={em.id}>{em.full_name}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div><Label>Emergency Contact</Label><Input value={assign.emergency_contact} onChange={(ev) => setAssign({...assign, emergency_contact: ev.target.value})} className="mt-1"/></div>
                        <div><Label>Blood Group</Label><Input value={assign.blood_group} onChange={(ev) => setAssign({...assign, blood_group: ev.target.value})} className="mt-1" placeholder="O+"/></div>
                      </div>
                      <div><Label>Address</Label><Textarea value={assign.address} onChange={(ev) => setAssign({...assign, address: ev.target.value})} className="mt-1" rows={2}/></div>
                      <div><Label>Employee Type</Label>
                        <Select value={assign.employee_type} onValueChange={(v) => setAssign({...assign, employee_type: v})}>
                          <SelectTrigger className="mt-1"><SelectValue placeholder="Select"/></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="full_time">Full-time</SelectItem>
                            <SelectItem value="contract">Contract</SelectItem>
                            <SelectItem value="intern">Intern</SelectItem>
                            <SelectItem value="consultant">Consultant</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <DialogFooter><Button onClick={saveAssign} className="bg-slate-900 hover:bg-slate-800" data-testid="save-assign-btn">Save</Button></DialogFooter>
                  </DialogContent>
                </Dialog>
              )}
            </div>
          )}
        </div>

        {/* Profile Completion */}
        {completion && completion.percent < 100 && canEdit && (
          <Card className="mt-5 p-4 border-amber-200 bg-amber-50 shadow-none" data-testid="completion-card">
            <div className="flex items-center gap-3">
              <Sparkles className="w-4 h-4 text-amber-600"/>
              <div className="flex-1">
                <div className="text-sm font-semibold text-slate-900">Profile {completion.percent}% complete</div>
                <div className="text-xs text-slate-600 mt-0.5">Missing: {completion.missing.map(m => m.replace("_", " ")).join(", ")}</div>
                <div className="h-1.5 bg-white rounded-full overflow-hidden mt-2">
                  <div className="h-full bg-gradient-to-r from-amber-500 to-emerald-500 rounded-full transition-all duration-700" style={{ width: `${completion.percent}%` }}/>
                </div>
              </div>
            </div>
          </Card>
        )}
      </Card>

      <Tabs defaultValue="overview">
        <TabsList className="bg-slate-100 flex-wrap h-auto">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="personal">Personal</TabsTrigger>
          <TabsTrigger value="job">Job & Joining</TabsTrigger>
          <TabsTrigger value="payroll">Payroll / Statutory</TabsTrigger>
          <TabsTrigger value="bank">Bank</TabsTrigger>
          <TabsTrigger value="family">Family / Nominee</TabsTrigger>
          <TabsTrigger value="documents">Documents</TabsTrigger>
          <TabsTrigger value="reporting">Reporting</TabsTrigger>
          <TabsTrigger value="responsibilities">Responsibilities</TabsTrigger>
          <TabsTrigger value="skills">Skills</TabsTrigger>
          <TabsTrigger value="assets">Assets</TabsTrigger>
          <TabsTrigger value="team">Team</TabsTrigger>
          {canViewNotes && <TabsTrigger value="notes">Notes</TabsTrigger>}
        </TabsList>

        <TabsContent value="overview" className="mt-4 grid md:grid-cols-2 gap-4">
          <Card className="p-5 border-slate-200 shadow-none">
            <div className="text-xs uppercase tracking-[0.15em] font-semibold text-slate-500 mb-3">Quick Facts</div>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-slate-500">Employee ID</span><span className="font-mono text-slate-900">{e.id}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Status</span><span className="text-slate-900 capitalize">{e.employment_status}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Department</span><span className="text-slate-900">{dept?.name || "—"}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Location</span><span className="text-slate-900">{e.location || "—"}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Role</span><span className="text-slate-900 capitalize">{e.role.replace("_"," ")}</span></div>
            </div>
          </Card>
          <Card className="p-5 border-slate-200 shadow-none">
            <div className="text-xs uppercase tracking-[0.15em] font-semibold text-slate-500 mb-3">Leave Balance</div>
            <div className="grid grid-cols-3 gap-3">
              {Object.entries(e.leave_balance || {}).map(([k, v]) => (
                <div key={k} className="p-3 rounded-md bg-slate-50 border border-slate-200">
                  <div className="text-xs uppercase tracking-[0.15em] text-slate-500">{k}</div>
                  <div className="text-2xl font-[Outfit] font-semibold text-slate-900 mt-1">{v}</div>
                </div>
              ))}
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="personal" className="mt-4 grid md:grid-cols-2 gap-4">
          <Card className="p-5 border-slate-200 shadow-none">
            <div className="text-xs uppercase tracking-[0.15em] font-semibold text-slate-500 mb-3">Contact</div>
            <div className="space-y-2 text-sm">
              <div className="flex items-start gap-2"><Mail className="w-4 h-4 text-slate-400 mt-0.5"/><span>{e.email}</span></div>
              {e.phone && <div className="flex items-start gap-2"><Phone className="w-4 h-4 text-slate-400 mt-0.5"/><span>{e.phone}</span></div>}
              {e.emergency_contact && <div className="flex items-start gap-2"><Heart className="w-4 h-4 text-red-400 mt-0.5"/><span>Emergency: {e.emergency_contact}</span></div>}
              {e.address && <div className="flex items-start gap-2"><Home className="w-4 h-4 text-slate-400 mt-0.5"/><span>{e.address}</span></div>}
            </div>
          </Card>
          <Card className="p-5 border-slate-200 shadow-none">
            <div className="text-xs uppercase tracking-[0.15em] font-semibold text-slate-500 mb-3">Personal</div>
            <div className="space-y-2 text-sm">
              {e.blood_group && <div className="flex items-center gap-2"><Droplet className="w-4 h-4 text-red-400"/><span>Blood Group: <span className="font-medium">{e.blood_group}</span></span></div>}
              {e.date_of_birth && <div className="flex items-center gap-2"><Calendar className="w-4 h-4 text-slate-400"/><span>DOB: {new Date(e.date_of_birth).toLocaleDateString()}</span></div>}
              {e.employee_type && <div className="flex items-center gap-2"><User2 className="w-4 h-4 text-slate-400"/><span>Type: <span className="font-medium capitalize">{e.employee_type.replace("_"," ")}</span></span></div>}
              {!e.blood_group && !e.date_of_birth && !e.employee_type && <div className="text-sm text-slate-400">No personal details added yet.</div>}
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="job" className="mt-4">
          <Card className="p-5 border-slate-200 shadow-none">
            <div className="text-xs uppercase tracking-[0.15em] font-semibold text-slate-500 mb-3">Job & Joining</div>
            <div className="grid md:grid-cols-3 gap-4">
              <div><div className="text-xs text-slate-500">Designation</div><div className="text-base font-medium text-slate-900 mt-1">{e.designation || "—"}</div></div>
              <div><div className="text-xs text-slate-500">Department</div><div className="text-base font-medium text-slate-900 mt-1">{dept?.name || "—"}</div></div>
              <div><div className="text-xs text-slate-500">Location</div><div className="text-base font-medium text-slate-900 mt-1">{e.location || "—"}</div></div>
              <div><div className="text-xs text-slate-500">Joining Date</div><div className="text-base font-medium text-slate-900 mt-1">{e.joining_date ? new Date(e.joining_date).toLocaleDateString() : "—"}</div></div>
              <div><div className="text-xs text-slate-500">Role</div><div className="text-base font-medium text-slate-900 mt-1 capitalize">{e.role.replace("_"," ")}</div></div>
              <div><div className="text-xs text-slate-500">Status</div><div className="text-base font-medium text-slate-900 mt-1 capitalize">{e.employment_status}</div></div>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="payroll" className="mt-4 space-y-4">
          <Card className="p-5 border-slate-200 shadow-none">
            <div className="flex items-center justify-between mb-3">
              <div className="text-xs uppercase tracking-[0.15em] font-semibold text-slate-500">Statutory & Compliance</div>
              {canEdit && <Button size="sm" variant="outline" onClick={() => setEditStatutory(true)} data-testid="edit-statutory">Edit</Button>}
            </div>
            <div className="grid md:grid-cols-3 gap-4 text-sm">
              <KV label="PF Account Number" value={e.pf_number} mono />
              <KV label="UAN" value={e.uan} mono />
              <KV label="ESI Number" value={e.esi_number} mono />
              <KV label="PAN" value={e.pan} mono mask={!canEdit} />
              <KV label="Aadhaar" value={e.aadhaar} mono mask={!canEdit} />
              <KV label="Tax Regime" value={e.tax_regime ? e.tax_regime.toUpperCase() : null} />
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="bank" className="mt-4 space-y-4">
          <Card className="p-5 border-slate-200 shadow-none">
            <div className="flex items-center justify-between mb-3">
              <div className="text-xs uppercase tracking-[0.15em] font-semibold text-slate-500">Bank Details</div>
              {canEdit && <Button size="sm" variant="outline" onClick={() => setEditBank(true)} data-testid="edit-bank">Edit</Button>}
            </div>
            <div className="grid md:grid-cols-3 gap-4 text-sm">
              <KV label="Account Holder" value={e.bank_account_name} />
              <KV label="Account Number" value={e.bank_account_number} mono mask={!canEdit} />
              <KV label="IFSC" value={e.bank_ifsc} mono />
              <KV label="Bank" value={e.bank_name} />
              <KV label="Branch" value={e.bank_branch} />
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="family" className="mt-4 space-y-4">
          <Card className="p-5 border-slate-200 shadow-none">
            <div className="flex items-center justify-between mb-3">
              <div className="text-xs uppercase tracking-[0.15em] font-semibold text-slate-500">Family & Nominee</div>
              {canEdit && <Button size="sm" variant="outline" onClick={() => setEditFamily(true)} data-testid="edit-family">Edit</Button>}
            </div>
            <div className="grid md:grid-cols-3 gap-4 text-sm">
              <KV label="Father's name" value={e.father_name} />
              <KV label="Mother's name" value={e.mother_name} />
              <KV label="Spouse" value={e.spouse_name} />
              <KV label="Nominee" value={e.nominee_name} />
              <KV label="Nominee relation" value={e.nominee_relation} />
              <KV label="Nominee contact" value={e.nominee_contact} />
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="documents" className="mt-4 space-y-4">
          <Card className="p-5 border-slate-200 shadow-none">
            <div className="flex items-center justify-between mb-3">
              <div className="text-xs uppercase tracking-[0.15em] font-semibold text-slate-500">Documents Vault</div>
              {canEdit && <Button size="sm" variant="outline" onClick={() => window.location.href = `/documents?owner=${e.id}`} data-testid="upload-doc">Upload in Documents module</Button>}
            </div>
            <EmployeeDocuments employeeId={e.id} />
          </Card>
        </TabsContent>

        <TabsContent value="reporting" className="mt-4 grid md:grid-cols-2 gap-4">
          <Card className="p-5 border-slate-200 shadow-none">
            <div className="text-xs uppercase tracking-[0.15em] font-semibold text-slate-500 mb-3">Reporting Manager</div>
            {e.manager ? (
              <Link to={`/people/${e.manager.id}`} className="flex items-center gap-3 p-3 rounded-md bg-slate-50 hover:bg-slate-100 transition">
                <Avatar className="w-10 h-10"><AvatarFallback className="bg-slate-900 text-white text-xs">
                  {e.manager.full_name.split(" ").map(s => s[0]).slice(0,2).join("")}
                </AvatarFallback></Avatar>
                <div><div className="font-medium text-slate-900">{e.manager.full_name}</div><div className="text-xs text-slate-500">{e.manager.designation}</div></div>
              </Link>
            ) : <div className="text-sm text-slate-500">Top of hierarchy · no manager</div>}
          </Card>
          <Card className="p-5 border-slate-200 shadow-none">
            <div className="text-xs uppercase tracking-[0.15em] font-semibold text-slate-500 mb-3">Dotted-line Manager</div>
            {e.dotted_manager_id ? (
              <div className="text-sm text-slate-900">{emps.find(em => em.id === e.dotted_manager_id)?.full_name || "—"}</div>
            ) : <div className="text-sm text-slate-500">Not assigned</div>}
          </Card>
        </TabsContent>

        <TabsContent value="responsibilities" className="mt-4">
          <Card className="p-5 border-slate-200 shadow-none">
            <div className="text-xs uppercase tracking-[0.15em] font-semibold text-slate-500 mb-3">Assigned Responsibilities ({resp_assigned.length})</div>
            {resp_assigned.length === 0 ? (
              <div className="text-sm text-slate-500">No responsibilities assigned. {canAssign && "Click Assign to add."}</div>
            ) : (
              <div className="grid sm:grid-cols-2 gap-3">
                {resp_assigned.map(r => (
                  <div key={r.id} className="p-3 rounded-md bg-cyan-500/5 border border-cyan-500/30">
                    <div className="font-medium text-slate-900 text-sm">{r.name}</div>
                    <div className="text-xs text-slate-500 mt-0.5">{r.category}</div>
                    {r.description && <div className="text-xs text-slate-600 mt-1">{r.description}</div>}
                  </div>
                ))}
              </div>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="skills" className="mt-4">
          <Card className="p-5 border-slate-200 shadow-none">
            <div className="text-xs uppercase tracking-[0.15em] font-semibold text-slate-500 mb-3">Skills ({(e.skills || []).length})</div>
            <div className="flex flex-wrap gap-2">
              {(e.skills || []).map(s => <Badge key={s} className="bg-slate-900 text-white border-0">{s}</Badge>)}
              {(!e.skills || e.skills.length === 0) && <span className="text-sm text-slate-500">No skills listed. {canAssign && "Use Assign to add."}</span>}
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="assets" className="mt-4">
          <Card className="p-5 border-slate-200 shadow-none">
            <div className="text-xs uppercase tracking-[0.15em] font-semibold text-slate-500 mb-3">Assigned Assets ({(e.assets || []).length})</div>
            {(!e.assets || e.assets.length === 0) ? (
              <div className="text-sm text-slate-500">No assets assigned.</div>
            ) : (
              <div className="space-y-2">
                {e.assets.map((a) => (
                  <div key={a.id} className="flex items-center justify-between p-3 rounded-md bg-slate-50 border border-slate-200">
                    <div><div className="font-medium text-slate-900 text-sm">{a.asset_name}</div><div className="text-xs text-slate-500">{a.brand} · {a.serial_number}</div></div>
                    <Badge className="bg-cyan-100 text-cyan-700 border-0 capitalize">{a.status}</Badge>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="team" className="mt-4">
          <Card className="p-5 border-slate-200 shadow-none">
            <div className="text-xs uppercase tracking-[0.15em] font-semibold text-slate-500 mb-3">Direct Reports ({e.direct_reports?.length || 0})</div>
            {(!e.direct_reports || e.direct_reports.length === 0) ? (
              <div className="text-sm text-slate-500">No direct reports.</div>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {e.direct_reports.map((r) => (
                  <Link key={r.id} to={`/people/${r.id}`} className="flex items-center gap-3 p-3 rounded-md bg-slate-50 hover:bg-slate-100 transition">
                    <Avatar className="w-9 h-9"><AvatarFallback className="bg-slate-900 text-white text-xs">
                      {r.full_name.split(" ").map(s => s[0]).slice(0,2).join("")}
                    </AvatarFallback></Avatar>
                    <div><div className="font-medium text-slate-900 text-sm">{r.full_name}</div><div className="text-xs text-slate-500">{r.designation}</div></div>
                  </Link>
                ))}
              </div>
            )}
          </Card>
        </TabsContent>

        {canViewNotes && (
          <TabsContent value="notes" className="mt-4">
            <Card className="p-5 border-slate-200 shadow-none">
              <div className="text-xs uppercase tracking-[0.15em] font-semibold text-slate-500 mb-3">HR & Manager Notes ({notes.length})</div>
              <div className="flex gap-2 mb-4">
                <Input value={noteTxt} onChange={(ev) => setNoteTxt(ev.target.value)} placeholder="Add a note…" className="flex-1"/>
                <Button onClick={addNote} className="bg-slate-900 hover:bg-slate-800">Add</Button>
              </div>
              {notes.length === 0 ? <div className="text-sm text-slate-500">No notes yet.</div> : (
                <div className="space-y-2">
                  {notes.map(n => (
                    <div key={n.id} className="p-3 rounded-md bg-slate-50 border border-slate-200">
                      <div className="text-sm text-slate-900">{n.text}</div>
                      <div className="text-xs text-slate-400 mt-1">{new Date(n.created_at).toLocaleString()}</div>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </TabsContent>
        )}
      </Tabs>

      {/* ===== Statutory / Bank / Family edit dialogs ===== */}
      <EmployeeFieldsDialog open={editStatutory} onOpenChange={setEditStatutory} employee={e} title="Statutory & Compliance"
        fields={[
          { k: "pf_number", label: "PF Account Number" },
          { k: "uan",        label: "UAN" },
          { k: "esi_number", label: "ESI Number" },
          { k: "pan",        label: "PAN" },
          { k: "aadhaar",    label: "Aadhaar" },
          { k: "tax_regime", label: "Tax Regime", select: [["old","Old"],["new","New"]] },
        ]} onSaved={(d) => { setE(d); setEditStatutory(false); toast.success("Statutory details saved"); }} />

      <EmployeeFieldsDialog open={editBank} onOpenChange={setEditBank} employee={e} title="Bank Details"
        fields={[
          { k: "bank_account_name",   label: "Account holder name" },
          { k: "bank_account_number", label: "Account number" },
          { k: "bank_ifsc",           label: "IFSC" },
          { k: "bank_name",           label: "Bank" },
          { k: "bank_branch",         label: "Branch" },
        ]} onSaved={(d) => { setE(d); setEditBank(false); toast.success("Bank details saved"); }} />

      <EmployeeFieldsDialog open={editFamily} onOpenChange={setEditFamily} employee={e} title="Family & Nominee"
        fields={[
          { k: "father_name",     label: "Father's name" },
          { k: "mother_name",     label: "Mother's name" },
          { k: "spouse_name",     label: "Spouse name" },
          { k: "nominee_name",    label: "Nominee name" },
          { k: "nominee_relation",label: "Nominee relation" },
          { k: "nominee_contact", label: "Nominee contact" },
        ]} onSaved={(d) => { setE(d); setEditFamily(false); toast.success("Family details saved"); }} />
    </div>
  );
}

/* ==================== Small helpers ==================== */
function KV({ label, value, mono, mask }) {
  const display = mask && value ? value.slice(0, 2) + "••••" + (value.length > 4 ? value.slice(-2) : "") : value;
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
      <div className={`mt-0.5 text-slate-900 ${mono ? "font-mono" : ""} ${!value ? "text-slate-400 italic" : ""}`}>
        {value ? display : "Not provided"}
      </div>
    </div>
  );
}

function EmployeeDocuments({ employeeId }) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try { const { data } = await api.get(`/employees/${employeeId}/documents`); setDocs(data || []); }
      catch { setDocs([]); }
      finally { setLoading(false); }
    })();
  }, [employeeId]);
  if (loading) return <div className="text-sm text-slate-400">Loading…</div>;
  if (docs.length === 0) return (
    <div className="text-sm text-slate-500 p-6 border-2 border-dashed border-slate-200 rounded-lg text-center">
      No documents uploaded yet.<br/>
      <span className="text-xs">Standard docs: Resume · Offer letter · ID proof · Address proof · PAN · PF docs · Bank proof · Certificates</span>
    </div>
  );
  return (
    <div className="grid md:grid-cols-2 gap-2">
      {docs.map(d => (
        <a key={d.id} href={d.url || "#"} target="_blank" rel="noreferrer" className="flex items-center gap-2 p-2.5 rounded-md border border-slate-200 hover:bg-slate-50 hover:border-cyan-300 transition" data-testid={`doc-${d.id}`}>
          <div className="w-8 h-8 rounded bg-slate-100 flex items-center justify-center text-slate-500 text-xs font-bold">{(d.type || "DOC").slice(0,3).toUpperCase()}</div>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-medium text-slate-900 truncate">{d.name || d.title}</div>
            <div className="text-[10px] text-slate-500">{d.uploaded_at ? new Date(d.uploaded_at).toLocaleDateString() : ""}</div>
          </div>
        </a>
      ))}
    </div>
  );
}

function EmployeeFieldsDialog({ open, onOpenChange, title, fields, employee, onSaved }) {
  const [form, setForm] = useState({});
  useEffect(() => {
    if (!open || !employee) return;
    const init = {}; fields.forEach(f => { init[f.k] = employee[f.k] || ""; }); setForm(init);
  }, [open, employee, fields]);
  const save = async () => {
    try {
      const { data } = await api.patch(`/v2/employees/${employee.id}`, form);
      onSaved?.(data);
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed to save"); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl" data-testid={`dialog-${title.replace(/\s+/g, "-").toLowerCase()}`}>
        <DialogHeader><DialogTitle>{title}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {fields.map(f => (
            <div key={f.k}>
              <Label className="text-xs">{f.label}</Label>
              {f.select ? (
                <select value={form[f.k] || ""} onChange={(e) => setForm({...form, [f.k]: e.target.value})} className="mt-1 w-full h-9 rounded-md border border-slate-200 bg-white text-sm px-2" data-testid={`field-${f.k}`}>
                  <option value="">Select…</option>
                  {f.select.map(([v,l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              ) : (
                <Input value={form[f.k] || ""} onChange={(e) => setForm({...form, [f.k]: e.target.value})} className="mt-1 h-9" data-testid={`field-${f.k}`}/>
              )}
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button className="bg-slate-900 hover:bg-slate-800" onClick={save} data-testid="save-fields">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
